import { addDays, differenceInDays } from 'date-fns';
import type { Profile } from 'reactApp/modules/profile/profile.types';
import type { RootState } from 'reactApp/store';
import { createSelector } from 'reselect';

export const getProfile = (state: RootState): Profile => state.profile;

export const getCountDaysBeforeClear = createSelector(getProfile, (profile) => {
    const clearTime = profile?.overquota?.clearTime;
    const now = Date.now();

    return clearTime && now < clearTime ? differenceInDays(clearTime, now) : 0;
});

export const needToShowClearOverquotaNotification = createSelector(getProfile, (profile) => {
    const clearStarted = profile?.overquota?.clearStarted || 0;
    const now = Date.now();
    const finishNotificationsDate = addDays(clearStarted, 10).valueOf();

    return !!clearStarted && now > clearStarted && now < finishNotificationsDate;
});

export const getClearTime = createSelector(getProfile, (profile) => profile?.overquota?.clearTime);

export const getIsCheckUpdateLicenseCompleted = (state: RootState) => getProfile(state).checkUpdateLicenseCompleted;
export const getProfileVersion = (state: RootState) => getProfile(state).version;

export const getUserOverquotaState = createSelector(getProfile, (profile) => ({
    ...profile.overquota,
}));

export const getUserOverquotaCurrentState = createSelector(getProfile, (profile) => profile.overquota?.state);
export const getUserOverquotaCycleCount = createSelector(getProfile, (profile) => profile.overquota?.cycleCount || 0);
export const getIsUserOverquotaProfileLoaded = createSelector(getProfile, (profile) => profile.overquota?.status === 'loaded');
export const getUserOverquotaDates = createSelector(getProfile, (profile) => ({
    clearTime: profile.overquota?.clearTime,
    blockLetterDate: profile.overquota?.blockTime,
    startPipeline: profile.overquota?.startPipeline,
}));

export const getUserDevices = createSelector(getProfile, (profile) => profile.devices);
export const getUserMobilePlatforms = createSelector(getUserDevices, (devices) => {
    if (!devices) {
        return [];
    }

    return Object.values(devices)
        .map((device) => device.platform)
        .filter((platform) => !['Web', 'DiskO'].includes(platform));
});

export const getUserCreationTime = createSelector(getProfile, (profile) => profile.ctime);
export const getDaysAfterRegistration = createSelector(getUserCreationTime, (ctime) => {
    const now = Date.now();
    return differenceInDays(now, ctime);
});

export const getProfileLoadingState = createSelector(getProfile, (profile) => ({
    isLoading: profile.isLoading,
    isLoaded: profile.isLoaded,
}));
